import { useQuery } from "@tanstack/react-query";
import { projectionService, workQueueService } from "@/core/api/services";
import { workbenchKeys } from "@/core/api/queryKeys";
import { useAuth } from "@hub/auth";
import { parseProjectionData } from "./projectionUtils";

interface DailyStats {
  date: string;
  completed: number;
  avgHandleTimeMinutes: number;
}

interface PersonalStats {
  completedToday: number;
  completedThisWeek: number;
  avgHandleTimeMinutes: number;
  approachingSla: number;
  dailyTrend: DailyStats[];
}


export function usePersonalStats() {
  const { user } = useAuth();
  const userId = user?.id ?? "";

  const query = useQuery({
    queryKey: workbenchKeys.dashboard.personal(userId),
    queryFn: async (): Promise<PersonalStats> => {
      const [projResponse, queueResponse] = await Promise.all([
        projectionService.query({
          type: "workqueue_worker_daily",
          filters: { worker_id: userId },
          orderBy: "date",
          descending: true,
          limit: 7,
          offset: 0,
        }),
        workQueueService.listWorkItems({
          statuses: ["open", "in_progress"],
          claimedBy: userId,
          sortBy: "due_at",
          sortDesc: false,
          pageSize: 100,
          types: [],
          tags: {},
          pageToken: "",
        }),
      ]);

      const projections = projResponse.projections ?? [];
      const dailyTrend: DailyStats[] = projections.map((p) => {
        const d = parseProjectionData(p.data);
        return {
          date: (d.date as string) ?? "",
          completed: (d.completed as number) ?? 0,
          avgHandleTimeMinutes: (d.avg_handle_time_minutes as number) ?? 0,
        };
      }).reverse();

      const today = new Date().toISOString().slice(0, 10);
      const todayStats = dailyTrend.find((d) => d.date === today);

      const now = new Date();
      const slaThreshold = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      const items = queueResponse.items ?? [];
      const approachingSla = items.filter((item) => {
        if (!item.dueAt) return false;
        const dueDate = new Date(Number(item.dueAt.seconds) * 1000);
        return dueDate <= slaThreshold && dueDate > now;
      }).length;

      return {
        completedToday: todayStats?.completed ?? 0,
        completedThisWeek: dailyTrend.reduce((sum, d) => sum + d.completed, 0),
        avgHandleTimeMinutes: todayStats?.avgHandleTimeMinutes ?? 0,
        approachingSla,
        dailyTrend,
      };
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  return {
    stats: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}
